import { throwForFailedHttpResponse } from './http-client-errors';
/**
 * Generate a client sdk conforming to an API interface\
 * The `I` generic type is the service API interface\
 * This implementation is for gateways SDKs, for workers see `@msl/framework`
 * @param options Available options, see comments on available props
 * @returns A runtime client implementing the API
 */
export function generateHttpClient(options) {
    return new GenericSdk(options);
}
class GenericSdk {
    options;
    baseUrl;
    _auth;
    requireAuth;
    constructor(options) {
        this.options = options;
        const { methods, requireAuth } = options;
        // Generate methods dynamically for all API methods
        methods.forEach((method) => {
            // Wire each to the generic `sdkMethod`
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this[method] = (request) => this.sdkMethod(method, request);
        });
        // NOTE!
        // The above code must be executed at construction
        // Otherwise the jest mocks won't work
        if (Array.isArray(requireAuth)) {
            this.requireAuth = new Set(requireAuth);
        }
        else if (requireAuth) {
            this.requireAuth = new Set(methods); // Just add all
        }
    }
    /**
     *
     * @param localPort Intended ONLY for service test
     */
    async init(auth, localPort) {
        this.baseUrl = await this.getBaseUrl(localPort);
        this._auth = auth;
        console.log(`${this.options.sdkPackageName} Initialized`);
    }
    async getBaseUrl(customPort) {
        const { baseRoute } = this.options;
        if (customPort) {
            return `http://localhost:${customPort}/${baseRoute}`;
        }
        // Direct all calls to ws1 local-dev-server when running locally
        if (window.location.host.includes('local')) {
            return `http://${window.location.hostname}:37000/${baseRoute}`;
        }
        // Relative path to the domain root (cloud env)
        return `${import.meta.env.VITE_API_HOSTNAME}/${baseRoute}`;
    }
    checkInit() {
        if (!this.baseUrl)
            throw new Error(`${this.options.sdkPackageName} Not initialized`);
    }
    /**
     * Perform a POST request
     *
     * CONSIDER: Auto logs and metric (configurable with a flag)
     * @param method The route to call
     * @param request The request POJO to stringify
     * @returns The response as a POJO (or throws an error)
     */
    async sdkMethod(method, request) {
        this.checkInit();
        const url = `${this.baseUrl}/${method}`;
        console.log({
            message: `${this.options.sdkPackageName}: Calling ${method}`,
            request,
        });
        const body = JSON.stringify(request);
        const headers = {
            'content-type': 'application/json',
        };
        const requireAuth = this.requireAuth?.has(method);
        if (requireAuth) {
            if (!this._auth) {
                throw new Error(`${this.options.sdkPackageName} is missing iAuth`);
            }
            const accessToken = await this.getAccessToken();
            headers['ws-auth'] = accessToken;
        }
        let resp = await this.doFetch(url, body, headers);
        // Try to refresh token and retry (once)
        if (resp.status === 401 && requireAuth) {
            headers['ws-auth'] = (await this._auth?.refresh()) || '';
            resp = await this.doFetch(url, body, headers);
        }
        if (!resp.ok) {
            await throwForFailedHttpResponse(url, resp, () => resp.text(), {
                includeResponseBodyInMessage: !!this.baseUrl?.includes('localhost'),
            });
        }
        return await resp.json();
    }
    async doFetch(url, body, headers) {
        return await fetch(url, {
            method: 'POST',
            body,
            headers,
            mode: 'cors',
            redirect: 'follow', // need this only on local
            credentials: 'include',
        });
    }
    async getAccessToken() {
        if (!this._auth)
            return undefined;
        const token = this._auth.getCurrentAccessToken();
        if (token)
            return token;
        return await this._auth.refresh();
    }
}
//# sourceMappingURL=http-client-esm.js.map